import { useEffect, useMemo, useState } from "react";
import { Share2, X, Users, GraduationCap, Search, Check, Link as LinkIcon } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * ShareAssessmentModal
 * Trainer picks students (individual) and/or batches to share an assessment with.
 * On save, the /api/assessments/:id/share endpoint is called with the exact target set.
 */
export default function ShareAssessmentModal({ assessment, onClose, onSaved }) {
  const [students, setStudents] = useState([]);
  const [batches, setBatches] = useState([]);
  const [pickedUsers, setPickedUsers] = useState(new Set());
  const [pickedBatches, setPickedBatches] = useState(new Set());
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("users"); // users | batches
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [reach, setReach] = useState(0);

  useEffect(() => {
    if (!assessment) return;
    setLoading(true);
    Promise.all([
      api.get("/users?role=student"),
      api.get("/batches"),
      api.get(`/assessments/${assessment.id}/share`),
    ]).then(([su, ba, sh]) => {
      setStudents(su.data);
      setBatches(ba.data);
      setPickedUsers(new Set(sh.data.user_ids || []));
      setPickedBatches(new Set(sh.data.batch_ids || []));
      setReach(sh.data.effective_reach || 0);
    }).finally(() => setLoading(false));
  }, [assessment]);

  const filteredStudents = useMemo(() => {
    const s = q.toLowerCase();
    if (!s) return students;
    return students.filter((u) =>
      (u.name || "").toLowerCase().includes(s) ||
      (u.email || "").toLowerCase().includes(s) ||
      (u.employee_id || "").toLowerCase().includes(s)
    );
  }, [students, q]);

  const totalReach = useMemo(() => {
    // Union of directly-picked users + members of picked batches (local estimate)
    const set = new Set(pickedUsers);
    for (const u of students) {
      if (u.batch_id && pickedBatches.has(u.batch_id)) set.add(u.id);
    }
    return set.size;
  }, [pickedUsers, pickedBatches, students]);

  const toggleUser = (id) => {
    const s = new Set(pickedUsers);
    s.has(id) ? s.delete(id) : s.add(id);
    setPickedUsers(s);
  };
  const toggleBatch = (id) => {
    const s = new Set(pickedBatches);
    s.has(id) ? s.delete(id) : s.add(id);
    setPickedBatches(s);
  };
  const selectAllStudents = () => setPickedUsers(new Set(filteredStudents.map((u) => u.id)));
  const clearAllUsers = () => setPickedUsers(new Set());

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/assessments/${assessment.id}/share`, {
        user_ids: Array.from(pickedUsers),
        batch_ids: Array.from(pickedBatches),
      });
      setReach(data.effective_reach || 0);
      toast.success(
        totalReach === 0
          ? `Sharing removed for “${assessment.title}”`
          : `Shared with ${data.effective_reach} employee${data.effective_reach === 1 ? "" : "s"}`
      );
      onSaved?.(data);
      onClose();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to save share settings");
    } finally {
      setSaving(false);
    }
  };

  const copyShareLink = async () => {
    const url = `${window.location.origin}/student/exam/${assessment.id}/take`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Shareable link copied to clipboard");
    } catch {
      window.prompt("Copy this link:", url);
    }
  };

  if (!assessment) return null;

  return (
    <div
      data-testid="share-modal"
      className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center px-4 py-6"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl flex flex-col max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5 border-b border-slate-100 flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-blue-50 grid place-items-center flex-shrink-0">
            <Share2 className="w-5 h-5 text-blue-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-semibold text-slate-900 truncate">Share “{assessment.title}”</h2>
            <p className="mt-0.5 text-sm text-slate-500">
              Pick specific employees or batches. Only people you share with will be able to see and take this test.
            </p>
            <button
              type="button"
              onClick={copyShareLink}
              data-testid="share-copy-link"
              className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-blue-700 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-full px-3 py-1">
              <LinkIcon className="w-3 h-3" /> Copy shareable link
            </button>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 p-1" data-testid="share-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 pt-3">
          <div className="flex gap-1 bg-slate-100 rounded-lg p-1 w-fit">
            <TabBtn active={tab === "users"} onClick={() => setTab("users")} testId="share-tab-users">
              <Users className="w-3.5 h-3.5" /> Employees
              <span className="ml-1 text-[10px] font-semibold text-slate-500">({pickedUsers.size})</span>
            </TabBtn>
            <TabBtn active={tab === "batches"} onClick={() => setTab("batches")} testId="share-tab-batches">
              <GraduationCap className="w-3.5 h-3.5" /> Batches
              <span className="ml-1 text-[10px] font-semibold text-slate-500">({pickedBatches.size})</span>
            </TabBtn>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 min-h-0">
          {loading ? (
            <div className="text-center text-sm text-slate-500 py-10">Loading…</div>
          ) : tab === "users" ? (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <div className="relative flex-1">
                  <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    data-testid="share-user-search"
                    placeholder="Search by name / email / employee ID"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    className="w-full border border-slate-300 rounded-lg pl-9 pr-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                  />
                </div>
                <button
                  data-testid="share-select-all"
                  onClick={selectAllStudents}
                  disabled={filteredStudents.length === 0}
                  className="text-xs font-medium text-blue-700 border border-blue-200 bg-blue-50 hover:bg-blue-100 rounded-lg px-3 py-2 disabled:opacity-50">
                  Select all
                </button>
                {pickedUsers.size > 0 && (
                  <button
                    data-testid="share-clear-users"
                    onClick={clearAllUsers}
                    className="text-xs font-medium text-slate-600 border border-slate-200 hover:bg-slate-50 rounded-lg px-3 py-2">
                    Clear
                  </button>
                )}
              </div>

              {filteredStudents.length === 0 ? (
                <div className="text-center text-sm text-slate-500 py-10">
                  No employees match your search.
                </div>
              ) : (
                <ul className="border border-slate-200 rounded-lg divide-y divide-slate-100 max-h-[45vh] overflow-y-auto">
                  {filteredStudents.map((u) => {
                    const on = pickedUsers.has(u.id);
                    const batch = batches.find((b) => b.id === u.batch_id);
                    return (
                      <li key={u.id}>
                        <button
                          type="button"
                          onClick={() => toggleUser(u.id)}
                          data-testid={`share-user-${u.id}`}
                          className={`w-full text-left flex items-center gap-3 px-4 py-2.5 transition ${on ? "bg-blue-50" : "hover:bg-slate-50"}`}>
                          <div className={`w-5 h-5 rounded border flex-shrink-0 grid place-items-center ${on ? "bg-blue-600 border-blue-600" : "border-slate-300 bg-white"}`}>
                            {on && <Check className="w-3.5 h-3.5 text-white" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-slate-900 truncate">{u.name}</div>
                            <div className="text-xs text-slate-500 truncate">
                              {u.employee_id && <span className="font-mono-acc mr-2">{u.employee_id}</span>}
                              {u.email}
                              {batch && <span className="ml-2 text-slate-400">· {batch.batch_name}</span>}
                            </div>
                          </div>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          ) : (
            <div>
              {batches.length === 0 ? (
                <div className="text-center text-sm text-slate-500 py-10">No batches configured.</div>
              ) : (
                <ul className="border border-slate-200 rounded-lg divide-y divide-slate-100">
                  {batches.map((b) => {
                    const on = pickedBatches.has(b.id);
                    const size = students.filter((u) => u.batch_id === b.id).length;
                    return (
                      <li key={b.id}>
                        <button
                          type="button"
                          onClick={() => toggleBatch(b.id)}
                          data-testid={`share-batch-${b.id}`}
                          className={`w-full text-left flex items-center gap-3 px-4 py-2.5 transition ${on ? "bg-blue-50" : "hover:bg-slate-50"}`}>
                          <div className={`w-5 h-5 rounded border flex-shrink-0 grid place-items-center ${on ? "bg-blue-600 border-blue-600" : "border-slate-300 bg-white"}`}>
                            {on && <Check className="w-3.5 h-3.5 text-white" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-slate-900 truncate">{b.batch_name}</div>
                            <div className="text-xs text-slate-500">
                              {size} employee{size === 1 ? "" : "s"}
                              {b.batch_type && <span className="ml-2 text-slate-400">· {b.batch_type}</span>}
                            </div>
                          </div>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}
        </div>

        <div className="border-t border-slate-100 px-5 py-3 flex items-center justify-between gap-3 flex-wrap">
          <div className="text-sm text-slate-600" data-testid="share-reach">
            {totalReach === 0
              ? "No one selected yet — test will not be shared"
              : (<><b className="text-slate-900">{totalReach}</b> employee{totalReach === 1 ? "" : "s"} will receive access</>)}
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} disabled={saving}
              className="px-4 py-2 border border-slate-300 hover:bg-slate-50 rounded-lg text-sm font-medium disabled:opacity-50">
              Cancel
            </button>
            <button
              data-testid="share-save"
              onClick={save}
              disabled={saving}
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium disabled:opacity-50">
              <Share2 className="w-4 h-4" />
              {saving ? "Saving…" : "Save & share"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function TabBtn({ active, onClick, children, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition ${active ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"}`}>
      {children}
    </button>
  );
}
